import axios from "axios";
import { APIV2 } from "../../../backend";
import { useAppContext } from "../../../hooks/context/AppContext";
import { useState, useMemo } from "react";
import useSilentIdTokenRefresh from "../../../msal/useSilentTokenRefresh";

// Common error handler
const handleError = (error: unknown, context: string) => {
  console.error(`Error ${context}:`, error);
  return null;
};

export const useCMOInnovationForumApiClient = () => {
  const { authData } = useAppContext();
  const [idToken, setIdToken] = useState(null);
  const getIdTokenSilently = useSilentIdTokenRefresh(
    (token: any | null, account: any | null) => {
      setIdToken(token);
    }
  );

  const apiClient = useMemo(() => {
    const instance = axios.create({
      baseURL: APIV2,
      headers: {
        "Content-Type": "application/json",
        Authorization: idToken
          ? `Bearer ${idToken}`
          : `Bearer ${authData.idToken}`,
      },
    });

    // Add response interceptor to handle 401 errors
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // If the error is 401 and we haven't already retried
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          try {
            const { token } = await getIdTokenSilently();
            if (token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            }
          } catch (refreshError) {
            console.error("Error refreshing token:", refreshError);
            // If token refresh fails, redirect to login or handle as needed
            // You might want to add your auth redirect logic here
            window.location.reload();
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
    return instance;
  }, [idToken, authData.idToken]);

  const getCMOInnovationForumById = async (id: any) => {
    try {
      const response = await apiClient.get(`/cmoinnovationforum/${id}`);
      return response.data;
    } catch (err) {
      return handleError(err, "fetching CMO Innovation Forum");
    }
  };

  const createCMOInnovationForum = async (payload: any, project_key: any) => {
    try {
      const response = await apiClient.post(
        `/cmoinnovationforum/${project_key}`,
        payload
      );
      return response.data;
    } catch (err) {
      return handleError(err, "creating technical scope");
    }
  };

  const updateCMOInnovationForum = async (payload: any, project_key: any) => {
    try {
      const response = await apiClient.put(
        `/cmoinnovationforum/${project_key}`,
        payload
      );
      return response.data;
    } catch (err) {
      return handleError(err, "updating technical scope");
    }
  };

  return {
    createCMOInnovationForum,
    updateCMOInnovationForum,
    getCMOInnovationForumById,
  };
};
